import React, { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, Image as ImageIcon, Music, MapPin } from "lucide-react";
import { loadPannellum } from "../../components/Pano360Viewer";

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

async function uploadMedia(file, category) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("category", category);
  const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

/**
 * Admin authoring UI for a multi-scene 360° tour (rooms linked by clickable
 * hotspots) -- writes back a tour_360 object matching what Tour360Viewer.jsx
 * (public side) expects. Click on the preview panorama to drop a hotspot at
 * that exact spot, pick which scene it should jump to, done.
 */
export default function Tour360EditorModal({ value, onSave, onClose }) {
  const [scenes, setScenes] = useState(() => (value?.scenes?.length ? value.scenes.map((s) => ({ ...s })) : []));
  const [firstScene, setFirstScene] = useState(value?.first_scene || null);
  const [audioUrl, setAudioUrl] = useState(value?.audio_url || "");
  const [activeSceneId, setActiveSceneId] = useState(value?.scenes?.[0]?.id || null);
  const [uploadingScene, setUploadingScene] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [pendingHotspot, setPendingHotspot] = useState(null); // {pitch, yaw}
  const [hotspotTarget, setHotspotTarget] = useState("");
  const [hotspotLabel, setHotspotLabel] = useState("");

  const containerRef = useRef(null);
  const viewerRef = useRef(null);

  const activeScene = scenes.find((s) => s.id === activeSceneId) || null;

  // (Re)initialize the preview viewer whenever the active scene's image or
  // hotspot list changes. A full reinit (rather than imperative addHotSpot/
  // removeHotSpot calls) keeps Pannellum's internal state always a direct
  // reflection of React state -- simpler to reason about for an editing UI
  // that isn't performance-sensitive.
  useEffect(() => {
    if (!activeScene || !containerRef.current) return;
    let cancelled = false;
    setPendingHotspot(null);
    loadPannellum()
      .then(() => {
        if (cancelled || !containerRef.current || !window.pannellum) return;
        try { viewerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
        viewerRef.current = window.pannellum.viewer(containerRef.current, {
          type: "equirectangular",
          panorama: activeScene.image,
          autoLoad: true,
          compass: false,
          showZoomCtrl: true,
          hfov: activeScene.hfov || 100,
          pitch: activeScene.pitch || 0,
          yaw: activeScene.yaw || 0,
          hotSpots: (activeScene.hotspots || []).map((h) => ({
            pitch: h.pitch,
            yaw: h.yaw,
            type: "info",
            text: h.text || `→ ${scenes.find((s) => s.id === h.target_scene_id)?.name || "?"}`,
          })),
        });
        viewerRef.current.on("mousedown", (e) => {
          const coords = viewerRef.current.mouseEventToCoords(e);
          setPendingHotspot({ pitch: coords[0], yaw: coords[1] });
          setHotspotTarget("");
          setHotspotLabel("");
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try { viewerRef.current?.destroy?.(); } catch (_e) { /* noop */ }
      viewerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSceneId, activeScene?.image, JSON.stringify(activeScene?.hotspots)]);

  const addScene = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingScene(true);
    try {
      const result = await uploadMedia(file, "pano360");
      const name = window.prompt("Name this area (e.g. 'Living Room', 'Entrance')", `Area ${scenes.length + 1}`) || `Area ${scenes.length + 1}`;
      const newScene = { id: uid(), name, image: `${process.env.REACT_APP_BACKEND_URL}${result.public_url}`, pitch: 0, yaw: 0, hfov: 100, hotspots: [] };
      const next = [...scenes, newScene];
      setScenes(next);
      setActiveSceneId(newScene.id);
      if (!firstScene) setFirstScene(newScene.id);
      toast.success("Scene added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingScene(false);
      e.target.value = "";
    }
  };

  const removeScene = (id) => {
    if (!window.confirm("Remove this scene? Any hotspots pointing to it from other scenes will stop working.")) return;
    const next = scenes.filter((s) => s.id !== id);
    setScenes(next);
    if (activeSceneId === id) setActiveSceneId(next[0]?.id || null);
    if (firstScene === id) setFirstScene(next[0]?.id || null);
  };

  const renameScene = (id, name) => {
    setScenes(scenes.map((s) => (s.id === id ? { ...s, name } : s)));
  };

  const saveHotspot = () => {
    if (!pendingHotspot || !hotspotTarget) return;
    const hs = { pitch: pendingHotspot.pitch, yaw: pendingHotspot.yaw, target_scene_id: hotspotTarget, text: hotspotLabel || `Go to ${scenes.find((s) => s.id === hotspotTarget)?.name || "next area"}` };
    setScenes(scenes.map((s) => (s.id === activeSceneId ? { ...s, hotspots: [...(s.hotspots || []), hs] } : s)));
    setPendingHotspot(null);
    setHotspotTarget("");
    setHotspotLabel("");
  };

  const removeHotspot = (idx) => {
    setScenes(scenes.map((s) => (s.id === activeSceneId ? { ...s, hotspots: (s.hotspots || []).filter((_, i) => i !== idx) } : s)));
  };

  const uploadAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAudio(true);
    try {
      const result = await uploadMedia(file, "audio");
      setAudioUrl(`${process.env.REACT_APP_BACKEND_URL}${result.public_url}`);
      toast.success("Audio uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingAudio(false);
      e.target.value = "";
    }
  };

  const save = () => {
    if (scenes.length === 0) {
      onSave(null); // no scenes = no tour
      onClose();
      return;
    }
    if (!firstScene || !scenes.some((s) => s.id === firstScene)) {
      toast.error("Pick a starting scene first");
      return;
    }
    onSave({ first_scene: firstScene, audio_url: audioUrl || null, scenes });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-3" data-testid="tour360-editor-modal">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="font-display text-lg font-bold text-[#1a3c5e]">360° Walkthrough Tour</div>
          <button onClick={onClose} data-testid="tour360-editor-close" className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 grid lg:grid-cols-[240px_1fr] gap-5">
          {/* Scene list */}
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">Scenes / Areas</div>
            {scenes.map((s) => (
              <div key={s.id} className={`rounded-lg border p-2 cursor-pointer ${activeSceneId === s.id ? "border-[#F7941D] bg-[#FEF3E2]/40" : "border-gray-200"}`} onClick={() => setActiveSceneId(s.id)} data-testid={`tour-scene-${s.id}`}>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                    <img src={s.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <input
                    value={s.name}
                    onChange={(e) => renameScene(s.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 min-w-0 text-sm font-semibold border-none focus:outline-none focus:ring-1 focus:ring-[#F7941D] rounded px-1"
                  />
                  <button onClick={(e) => { e.stopPropagation(); removeScene(s.id); }} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <label className="flex items-center gap-1 text-[10px] text-gray-500 mt-1">
                  <input type="radio" name="firstScene" checked={firstScene === s.id} onChange={() => setFirstScene(s.id)} onClick={(e) => e.stopPropagation()} />
                  Starting scene
                </label>
              </div>
            ))}
            <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-[#F7941D]/40 rounded-lg py-3 text-xs font-semibold text-[#F7941D] cursor-pointer hover:bg-[#FEF3E2]/40 ${uploadingScene ? "opacity-50" : ""}`}>
              {uploadingScene ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {uploadingScene ? "Uploading..." : "Add Scene"}
              <input type="file" accept="image/jpeg" hidden disabled={uploadingScene} onChange={addScene} data-testid="tour-add-scene" />
            </label>
            <p className="text-[10px] text-gray-500">Equirectangular JPG, 2:1 ratio (e.g. a 360 camera photo).</p>

            <div className="pt-3 border-t border-gray-100 space-y-1.5">
              <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e] flex items-center gap-1"><Music className="w-3 h-3" /> Background Audio</div>
              {audioUrl && (
                <div className="flex items-center gap-1.5">
                  <audio src={audioUrl} controls className="w-full h-8" />
                  <button onClick={() => setAudioUrl("")} className="text-red-500 hover:bg-red-50 p-1 rounded flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
                </div>
              )}
              <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-2 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 ${uploadingAudio ? "opacity-50" : ""}`}>
                {uploadingAudio ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Music className="w-3.5 h-3.5" />}
                {uploadingAudio ? "Uploading..." : audioUrl ? "Replace Audio" : "Upload Narration"}
                <input type="file" accept="audio/*" hidden disabled={uploadingAudio} onChange={uploadAudio} data-testid="tour-add-audio" />
              </label>
            </div>
          </div>

          {/* Preview + hotspot placement */}
          <div className="space-y-3">
            {activeScene ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" /> Click anywhere on the panorama to drop a hotspot arrow there.
                </div>
                <div ref={containerRef} className="w-full h-[380px] rounded-lg overflow-hidden bg-black" data-testid="tour360-preview" />

                {pendingHotspot && (
                  <div className="bg-white border-2 border-[#F7941D] rounded-lg p-3 space-y-2" data-testid="tour-hotspot-form">
                    <div className="text-xs font-bold text-[#1a3c5e]">New hotspot at pitch {pendingHotspot.pitch.toFixed(1)}°, yaw {pendingHotspot.yaw.toFixed(1)}°</div>
                    <select value={hotspotTarget} onChange={(e) => setHotspotTarget(e.target.value)} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" data-testid="tour-hotspot-target">
                      <option value="">Link to scene...</option>
                      {scenes.filter((s) => s.id !== activeSceneId).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <input value={hotspotLabel} onChange={(e) => setHotspotLabel(e.target.value)} placeholder="Label (optional, e.g. 'Go to Kitchen')" className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm" data-testid="tour-hotspot-label" />
                    <div className="flex gap-2">
                      <button onClick={saveHotspot} disabled={!hotspotTarget} className="flex-1 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-40 text-white text-xs font-bold rounded-lg py-1.5" data-testid="tour-hotspot-save">Add Hotspot</button>
                      <button onClick={() => setPendingHotspot(null)} className="px-3 border border-gray-300 rounded-lg text-xs font-bold text-gray-600">Cancel</button>
                    </div>
                  </div>
                )}

                <div className="space-y-1">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#1a3c5e]">Hotspots in this scene</div>
                  {(activeScene.hotspots || []).length === 0 && <div className="text-xs text-gray-400 italic">None yet — click the panorama above to add one.</div>}
                  {(activeScene.hotspots || []).map((h, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-1.5 text-xs">
                      <span>{h.text} <span className="text-gray-400">→ {scenes.find((s) => s.id === h.target_scene_id)?.name || "(deleted scene)"}</span></span>
                      <button onClick={() => removeHotspot(i)} className="text-red-500 hover:bg-red-100 p-1 rounded"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[380px] flex items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                <div className="text-center">
                  <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Add your first scene to start building the tour
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 px-5 py-3 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-bold text-gray-700">Cancel</button>
          <button onClick={save} className="flex-1 bg-[#F7941D] hover:bg-[#e0861a] text-white rounded-xl py-2.5 text-sm font-bold" data-testid="tour360-editor-save">Save Tour</button>
        </div>
      </div>
    </div>
  );
}
